// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Responsible Gaming Page
// AGLC/AGCO MANDATED — Self-exclusion, limits, resources
// ================================================================
import React, { useState } from 'react';
import { useCasino } from '../context/CasinoShell';

export default function ResponsibleGamingPage() {
  const {
    sessionLimits, updateLimits, sessionStats,
    selfExclusion, activateSelfExclusion, isExcluded,
    getSessionDuration, COOLOFF_OPTIONS,
  } = useCasino();

  const [editLimits, setEditLimits] = useState({ ...sessionLimits });
  const [showExclusion, setShowExclusion] = useState(false);
  const [exclusionConfirm, setExclusionConfirm] = useState('');
  const [limitsSaved, setLimitsSaved] = useState(false);

  const handleSaveLimits = () => {
    updateLimits(editLimits);
    setLimitsSaved(true);
    setTimeout(() => setLimitsSaved(false), 3000);
  };

  const handleExclusion = (type) => {
    if (exclusionConfirm !== 'EXCLUDE') return;
    activateSelfExclusion(type);
    setShowExclusion(false);
    setExclusionConfirm('');
  };

  if (isExcluded()) {
    return (
      <div style={styles.container}>
        <div style={styles.excludedCard}>
          <span style={{ fontSize: 48 }}>🛑</span>
          <h2 style={styles.excludedTitle}>SELF-EXCLUSION ACTIVE</h2>
          <p style={styles.excludedText}>
            You have opted to self-exclude from Forge Casino.
            {selfExclusion?.type === 'permanent'
              ? ' This exclusion is permanent.'
              : ` This exclusion is active until ${new Date(selfExclusion?.until).toLocaleDateString()}.`}
          </p>
          <div style={styles.helpBox}>
            <h3 style={styles.helpTitle}>NEED HELP?</h3>
            <p style={styles.helpText}>Alberta Problem Gambling Helpline: <strong>1-866-332-2322</strong></p>
            <p style={styles.helpText}>ConnexOntario: <strong>1-866-531-2600</strong></p>
            <p style={styles.helpText}>National Council on Problem Gambling: <strong>1-800-522-4700</strong></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>🛡️ RESPONSIBLE GAMING</h1>
      <p style={styles.subtitle}>
        Gambling should be fun. Set your limits and play responsibly.
        These tools are available 24/7 as required by AGLC regulations.
      </p>

      {/* Session Summary */}
      <div style={styles.sessionCard}>
        <h3 style={styles.sectionTitle}>CURRENT SESSION</h3>
        <div style={styles.sessionGrid}>
          <div style={styles.sessionStat}>
            <span style={styles.sessionLabel}>Duration</span>
            <span style={styles.sessionValue}>{getSessionDuration()} min</span>
          </div>
          <div style={styles.sessionStat}>
            <span style={styles.sessionLabel}>Spins</span>
            <span style={styles.sessionValue}>{sessionStats.spinsThisSession}</span>
          </div>
          <div style={styles.sessionStat}>
            <span style={styles.sessionLabel}>Net Position</span>
            <span style={{
              ...styles.sessionValue,
              color: sessionStats.netPosition >= 0 ? '#00FF88' : '#DC2626',
            }}>
              {sessionStats.netPosition >= 0 ? '+' : ''}${sessionStats.netPosition.toFixed(2)}
            </span>
          </div>
          <div style={styles.sessionStat}>
            <span style={styles.sessionLabel}>Total Wagered</span>
            <span style={styles.sessionValue}>${sessionStats.totalBets.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Deposit Limits */}
      <div style={styles.limitsCard}>
        <h3 style={styles.sectionTitle}>DEPOSIT LIMITS</h3>
        <p style={styles.limitsNote}>Set maximum deposit amounts to control your spending</p>

        {[
          { key: 'depositDaily', label: 'Daily Limit' },
          { key: 'depositWeekly', label: 'Weekly Limit' },
          { key: 'depositMonthly', label: 'Monthly Limit' },
        ].map(limit => (
          <div key={limit.key} style={styles.limitRow}>
            <label style={styles.limitLabel}>{limit.label}</label>
            <div style={styles.limitInput}>
              <span style={styles.dollar}>$</span>
              <input
                style={styles.input}
                type="number"
                value={editLimits[limit.key] || ''}
                onChange={e => setEditLimits(prev => ({
                  ...prev,
                  [limit.key]: e.target.value ? parseFloat(e.target.value) : null,
                }))}
                placeholder="No limit"
                min="0"
              />
            </div>
          </div>
        ))}

        <h3 style={{ ...styles.sectionTitle, marginTop: 20 }}>LOSS LIMITS</h3>
        {[
          { key: 'lossDaily', label: 'Daily Loss Limit' },
          { key: 'lossSession', label: 'Session Loss Limit' },
        ].map(limit => (
          <div key={limit.key} style={styles.limitRow}>
            <label style={styles.limitLabel}>{limit.label}</label>
            <div style={styles.limitInput}>
              <span style={styles.dollar}>$</span>
              <input
                style={styles.input}
                type="number"
                value={editLimits[limit.key] || ''}
                onChange={e => setEditLimits(prev => ({
                  ...prev,
                  [limit.key]: e.target.value ? parseFloat(e.target.value) : null,
                }))}
                placeholder="No limit"
                min="0"
              />
            </div>
          </div>
        ))}

        <h3 style={{ ...styles.sectionTitle, marginTop: 20 }}>TIME LIMITS</h3>
        <div style={styles.limitRow}>
          <label style={styles.limitLabel}>Session Time Limit</label>
          <div style={styles.limitInput}>
            <input
              style={styles.input}
              type="number"
              value={editLimits.timeSession || ''}
              onChange={e => setEditLimits(prev => ({
                ...prev,
                timeSession: e.target.value ? parseInt(e.target.value) : null,
              }))}
              placeholder="No limit"
              min="0"
            />
            <span style={styles.unit}>min</span>
          </div>
        </div>
        <div style={styles.limitRow}>
          <label style={styles.limitLabel}>Reality Check Interval</label>
          <div style={styles.limitInput}>
            <input
              style={styles.input}
              type="number"
              value={editLimits.realityCheck || ''}
              onChange={e => setEditLimits(prev => ({
                ...prev,
                realityCheck: e.target.value ? parseInt(e.target.value) : null,
              }))}
              placeholder="30"
              min="5"
            />
            <span style={styles.unit}>min</span>
          </div>
        </div>

        <button style={styles.saveBtn} onClick={handleSaveLimits}>
          {limitsSaved ? '✅ LIMITS SAVED' : 'SAVE LIMITS'}
        </button>
      </div>

      {/* Self-Exclusion */}
      <div style={styles.exclusionCard}>
        <h3 style={styles.sectionTitle}>🛑 SELF-EXCLUSION</h3>
        <p style={styles.exclusionText}>
          If you need a break from gambling, you can exclude yourself from Forge Casino.
          During exclusion, you will not be able to play any games or deposit funds.
        </p>

        {!showExclusion ? (
          <button style={styles.exclusionBtn} onClick={() => setShowExclusion(true)}>
            I WANT TO SELF-EXCLUDE
          </button>
        ) : (
          <div style={styles.exclusionForm}>
            <p style={styles.exclusionWarning}>
              ⚠️ Self-exclusion cannot be reversed during the chosen period.
            </p>
            <div style={styles.exclusionOptions}>
              {Object.keys(COOLOFF_OPTIONS).map(type => (
                <button
                  key={type}
                  style={styles.exclusionOption}
                  onClick={() => {
                    if (exclusionConfirm === 'EXCLUDE') handleExclusion(type);
                  }}
                  disabled={exclusionConfirm !== 'EXCLUDE'}
                >
                  {type === '24h' ? '24 Hours' : type === '7d' ? '7 Days' : type === '30d' ? '30 Days' :
                    type === '6mo' ? '6 Months' : type === '1yr' ? '1 Year' : 'Permanent'}
                </button>
              ))}
            </div>
            <div style={styles.confirmRow}>
              <label style={styles.confirmLabel}>Type "EXCLUDE" to confirm:</label>
              <input
                style={styles.confirmInput}
                value={exclusionConfirm}
                onChange={e => setExclusionConfirm(e.target.value)}
                placeholder="EXCLUDE"
              />
            </div>
            <button style={styles.cancelBtn} onClick={() => { setShowExclusion(false); setExclusionConfirm(''); }}>
              CANCEL
            </button>
          </div>
        )}
      </div>

      {/* Help Resources */}
      <div style={styles.helpCard}>
        <h3 style={styles.sectionTitle}>HELP & RESOURCES</h3>
        <div style={styles.helpList}>
          <div style={styles.helpItem}>
            <strong>Alberta Problem Gambling Helpline</strong>
            <span>1-866-332-2322</span>
          </div>
          <div style={styles.helpItem}>
            <strong>ConnexOntario</strong>
            <span>1-866-531-2600</span>
          </div>
          <div style={styles.helpItem}>
            <strong>National Council on Problem Gambling</strong>
            <span>1-800-522-4700</span>
          </div>
          <div style={styles.helpItem}>
            <strong>Gamblers Anonymous</strong>
            <span>gamblersanonymous.org</span>
          </div>
        </div>
        <p style={styles.helpFooter}>
          If you or someone you know has a gambling problem, please reach out.
          Help is available 24/7.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 600, margin: '0 auto', minHeight: '100vh', background: '#0a0a0a' },
  title: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 2, margin: 0 },
  subtitle: { fontSize: 12, color: '#888', marginTop: 4, marginBottom: 20, lineHeight: 1.6 },
  sessionCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222', marginBottom: 16 },
  sectionTitle: { fontSize: 12, color: '#DC2626', fontWeight: 900, letterSpacing: 2, marginBottom: 12, marginTop: 0 },
  sessionGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 },
  sessionStat: { padding: 10, background: '#0a0a0a', borderRadius: 8, textAlign: 'center' },
  sessionLabel: { display: 'block', fontSize: 8, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 4 },
  sessionValue: { fontSize: 16, fontWeight: 900, color: '#fff', fontFamily: "'Courier New', monospace" },
  limitsCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222', marginBottom: 16 },
  limitsNote: { fontSize: 10, color: '#666', marginBottom: 12 },
  limitRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  limitLabel: { fontSize: 11, color: '#ccc', fontWeight: 600 },
  limitInput: { display: 'flex', alignItems: 'center', gap: 4 },
  dollar: { color: '#FFD700', fontWeight: 900, fontSize: 14 },
  input: {
    width: 100, padding: '6px 8px', borderRadius: 6, border: '1px solid #333',
    background: '#0a0a0a', color: '#fff', fontSize: 12, textAlign: 'right',
  },
  unit: { fontSize: 10, color: '#666' },
  saveBtn: {
    width: '100%', padding: 12, borderRadius: 8, border: '2px solid #00FF88',
    background: '#00FF8833', color: '#00FF88', fontSize: 12, fontWeight: 900,
    letterSpacing: 2, cursor: 'pointer', marginTop: 16,
  },
  exclusionCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #DC262644', marginBottom: 16 },
  exclusionText: { fontSize: 11, color: '#888', lineHeight: 1.6, marginBottom: 12 },
  exclusionBtn: {
    width: '100%', padding: 12, borderRadius: 8, border: '2px solid #DC2626',
    background: '#DC262233', color: '#DC2626', fontSize: 12, fontWeight: 900,
    letterSpacing: 2, cursor: 'pointer',
  },
  exclusionForm: { padding: 12, background: '#0a0000', borderRadius: 8, border: '1px solid #DC262644' },
  exclusionWarning: { fontSize: 11, color: '#FFD700', marginBottom: 12 },
  exclusionOptions: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 12 },
  exclusionOption: {
    padding: '8px 4px', borderRadius: 6, border: '1px solid #DC262666', background: '#DC262222',
    color: '#DC2626', fontSize: 10, fontWeight: 700, cursor: 'pointer',
  },
  confirmRow: { marginBottom: 12 },
  confirmLabel: { display: 'block', fontSize: 10, color: '#DC2626', marginBottom: 4 },
  confirmInput: {
    width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #DC262666',
    background: '#0a0000', color: '#DC2626', fontSize: 14, fontWeight: 900,
    letterSpacing: 3, textAlign: 'center', boxSizing: 'border-box',
  },
  cancelBtn: {
    width: '100%', padding: 8, borderRadius: 6, border: '1px solid #333',
    background: '#1a1a1a', color: '#888', fontSize: 10, cursor: 'pointer',
  },
  helpCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222', marginBottom: 16 },
  helpList: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 },
  helpItem: {
    display: 'flex', justifyContent: 'space-between', padding: '8px 10px',
    background: '#0a0a0a', borderRadius: 6, fontSize: 11, color: '#ccc',
  },
  helpFooter: { fontSize: 10, color: '#666', textAlign: 'center', lineHeight: 1.6 },
  excludedCard: {
    maxWidth: 480, margin: '60px auto', padding: 30, background: '#111',
    borderRadius: 16, border: '2px solid #DC2626', textAlign: 'center',
  },
  excludedTitle: { fontSize: 20, color: '#DC2626', fontWeight: 900, letterSpacing: 2, margin: '16px 0 8px' },
  excludedText: { fontSize: 13, color: '#aaa', lineHeight: 1.6, marginBottom: 20 },
  helpBox: { padding: 16, background: '#0a0a0a', borderRadius: 8, border: '1px solid #222' },
  helpTitle: { fontSize: 12, color: '#00FF88', fontWeight: 900, letterSpacing: 2, marginBottom: 10 },
  helpText: { fontSize: 11, color: '#ccc', margin: '6px 0' },
};
